// 면접 스케줄 모달 (SMS 메시지 페이지에서 열림). 위: 면접 추가 폼 / 아래: 주간 스케줄표.
// 칩 클릭 → 평가지 모달(InterviewEvalModal). 제목 옆 리스트 버튼 → 면접자 리스트 모달(InterviewListModal). 데이터: /dashboard/interview/*
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import { toast } from 'react-toastify';
import styles from '../../componentsStyle/interviewschedule.module.css';
import { getFetch, postFetch } from '../../../fetch/fetch';
import {
    DOW, JOBS, InterviewItem, InterviewPrefill, dateKey, dowIndex, fmtTime, gradeStr, hasGrades, mondayOf, parseKey, short3, shortLocation, sunbaeLocations
} from './interviewconstants';
import InterviewEvalModal from './interviewevalmodal';
import InterviewListModal from './interviewlistmodal';
import InterviewSelect from './interviewselect';

const schedBoxStyle: any = {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: 'calc(100% - 48px)', maxWidth: 1240, maxHeight: 'calc(100vh - 48px)', overflowY: 'auto',
    bgcolor: '#f2f2f2', borderRadius: '16px', boxShadow: 24, outline: 'none',
    p: '18px 20px 20px', display: 'flex', flexDirection: 'column', gap: '14px', boxSizing: 'border-box'
};

const CalendarIcon = ({ size = 16 }: { size?: number }) => (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#333333" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="5" width="16" height="16" rx="2"></rect><path d="M16 3v4M8 3v4M4 11h16"></path></svg>
);
const Chevron = ({ dir, size = 18 }: { dir: "left" | "right", size?: number }) => (
    <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#333333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={dir === "left" ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6"}></path></svg>
);

interface Props {
    open: boolean;
    onClose: () => void;
    user: any;
    prefill?: InterviewPrefill | null;
}

const InterviewScheduleModal: React.FC<Props> = ({ open, onClose, user, prefill }) => {

    const todayKey = dateKey(new Date());

    // 주간표
    const [weekStart, setWeekStart] = useState(() => dateKey(mondayOf(new Date())));
    const [items, setItems] = useState<InterviewItem[]>([]);
    const [selId, setSelId] = useState<number | null>(null);

    // 면접자 리스트 (최근 2개월 + 예정)
    const [listOpen, setListOpen] = useState(false);
    const [listItems, setListItems] = useState<InterviewItem[]>([]);
    const [listSince, setListSince] = useState("");

    // 면접 추가 폼
    const [fDate, setFDate] = useState(todayKey);
    const [fTime, setFTime] = useState("");
    const [fLocation, setFLocation] = useState("");
    const [fJob, setFJob] = useState("미정");
    const [fName, setFName] = useState("");
    const [fPhone, setFPhone] = useState("");
    const [err, setErr] = useState("");
    const [adding, setAdding] = useState(false);

    // 달력 드롭다운
    const [calOpen, setCalOpen] = useState(false);
    const [calY, setCalY] = useState(0);
    const [calM, setCalM] = useState(0);
    const calWrapRef = useRef<HTMLDivElement>(null);

    // 열릴 때마다 폼 초기화(문자방에서 열면 이름·지점·근무 미리 채움) + 이번 주로
    useEffect(() => {
        if (!open) return;
        const today = new Date();
        setWeekStart(dateKey(mondayOf(today)));
        setFDate(dateKey(today));
        setFTime("");
        setFName(prefill?.name || "");
        setFLocation(prefill?.location || "");
        setFJob(prefill?.job || "미정");
        setFPhone(prefill?.phone || "");
        setErr("");
        setSelId(null);
        setListOpen(false);
        setCalOpen(false);
    }, [open, prefill]);

    const fetchWeek = useCallback(async (start: string) => {
        try {
            const result: any = await getFetch(`/dashboard/interview/week?start=${start}`);
            if (result.message === "success") {
                setItems(result.data);
            } else {
                toast.error("면접 스케줄을 불러오지 못했습니다.");
            }
        } catch (e) {
            console.log(e);
        }
    }, []);

    useEffect(() => {
        if (open) fetchWeek(weekStart);
    }, [open, weekStart, fetchWeek]);

    const openList = async () => {
        setListOpen(true);
        try {
            const result: any = await getFetch("/dashboard/interview/list");
            if (result.message === "success") {
                setListItems(result.data);
                setListSince(result.since);
            } else {
                toast.error("면접자 리스트를 불러오지 못했습니다.");
            }
        } catch (e) {
            console.log(e);
        }
    };

    // 달력 바깥 클릭 시 닫힘
    useEffect(() => {
        if (!calOpen) return;
        const handler = (e: MouseEvent) => {
            if (calWrapRef.current && !calWrapRef.current.contains(e.target as Node)) setCalOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [calOpen]);

    const toggleCal = () => {
        const fd = parseKey(fDate);
        setCalY(fd.getFullYear());
        setCalM(fd.getMonth());
        setCalOpen(!calOpen);
    };
    const calMove = (delta: number) => {
        const d = new Date(calY, calM + delta, 1);
        setCalY(d.getFullYear());
        setCalM(d.getMonth());
    };

    const moveWeek = (delta: number) => {
        const d = parseKey(weekStart);
        setWeekStart(dateKey(new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7 * delta)));
    };

    const add = async () => {
        const names = fName.split(",").map((s) => s.trim()).filter(Boolean);
        if (!fTime) { setErr("시각을 입력하세요."); return; }
        if (!names.length) { setErr("이름을 한 명 이상 입력하세요."); return; }
        if (!fLocation) { setErr("지점을 고르세요."); return; }
        setAdding(true);
        try {
            const result: any = await postFetch("/dashboard/interview/create", {
                date: fDate, time: fTime, location: fLocation, job: fJob, names, phone: fPhone,
            });
            if (result.message === "success") {
                setFName("");
                setFPhone("");
                setErr("");
                // 추가한 날짜의 주로 이동 (같은 주면 다시 불러오기)
                const targetWeek = dateKey(mondayOf(parseKey(fDate)));
                if (targetWeek === weekStart) fetchWeek(weekStart);
                else setWeekStart(targetWeek);
            } else {
                setErr("추가에 실패했습니다.");
            }
        } catch (e) {
            console.log(e);
            setErr("추가에 실패했습니다.");
        } finally {
            setAdding(false);
        }
    };

    // 평가지에서 바뀐 내용은 주간표·리스트 둘 다 반영
    const onLocalChange = useCallback((id: number, patch: Partial<InterviewItem>) => {
        setItems((prev) => prev.map((it) => it.id === id ? { ...it, ...patch } : it));
        setListItems((prev) => prev.map((it) => it.id === id ? { ...it, ...patch } : it));
    }, []);
    const onDeleted = useCallback((id: number) => {
        setItems((prev) => prev.filter((it) => it.id !== id));
        setListItems((prev) => prev.filter((it) => it.id !== id));
        setSelId(null);
    }, []);

    // 요일 열 → 시각 묶음
    const days = useMemo(() => {
        const start = parseKey(weekStart);
        return DOW.map((w, i) => {
            const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
            const key = dateKey(d);
            const list = items.filter((it) => it.date === key);
            const times = Array.from(new Set(list.map((it) => it.time))).sort();
            const groups = times.map((t) => ({ time: t, people: list.filter((it) => it.time === t) }));
            return { key, label: `${w} ${d.getDate()}`, isToday: key === todayKey, groups };
        });
    }, [weekStart, items, todayKey]);

    const rangeLabel = useMemo(() => {
        const s = parseKey(weekStart);
        const e = new Date(s.getFullYear(), s.getMonth(), s.getDate() + 6);
        return `${s.getMonth() + 1}월 ${s.getDate()}일 – ${e.getMonth() + 1}월 ${e.getDate()}일`;
    }, [weekStart]);

    const fd = parseKey(fDate);
    const fDateLabel = `${fd.getMonth() + 1}월 ${fd.getDate()}일 (${DOW[dowIndex(fd)]})`;

    const calCells = useMemo(() => {
        const cells: (number | null)[] = [];
        const firstDow = dowIndex(new Date(calY, calM, 1));
        const dim = new Date(calY, calM + 1, 0).getDate();
        for (let i = 0; i < firstDow; i++) cells.push(null);
        for (let d = 1; d <= dim; d++) cells.push(d);
        return cells;
    }, [calY, calM]);

    const selItem = items.find((it) => it.id === selId) || listItems.find((it) => it.id === selId) || null;

    return (
        <Modal open={open} onClose={onClose} sx={{ zIndex: 600 }}>
            <Box sx={schedBoxStyle} className={styles.wrap} role="dialog" aria-label="면접 스케줄">
                <div className={styles.headerRow}>
                    <div className={styles.titleRow}>
                        <CalendarIcon size={20} />
                        <h2 className={styles.title}>면접 스케줄</h2>
                        <button className={styles.listBtn} onClick={openList} aria-label="면접자 전체 리스트" title="면접자 전체 리스트 (최근 2개월)">
                            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3346A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 6h13M8 12h13M8 18h13"></path><circle cx="4" cy="6" r="1" fill="#3346A6"></circle><circle cx="4" cy="12" r="1" fill="#3346A6"></circle><circle cx="4" cy="18" r="1" fill="#3346A6"></circle></svg>
                        </button>
                    </div>
                    <button className={styles.circleBtn} aria-label="닫기" onClick={onClose}>
                        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333333" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"></path></svg>
                    </button>
                </div>

                {/* 면접 추가 */}
                <section className={styles.card}>
                    <div className={styles.cardTitle}>면접 추가</div>
                    <div className={styles.formRow}>
                        <div className={styles.field} ref={calWrapRef}>날짜
                            <button className={styles.dateBtn} onClick={toggleCal}>
                                <CalendarIcon />
                                {fDateLabel}
                            </button>
                            {calOpen && (
                                <div className={styles.cal}>
                                    <div className={styles.calHead}>
                                        <button className={styles.circleBtn} aria-label="이전 달" onClick={() => calMove(-1)}><Chevron dir="left" size={14} /></button>
                                        <div className={styles.calTitle}>{calY}년 {calM + 1}월</div>
                                        <button className={styles.circleBtn} aria-label="다음 달" onClick={() => calMove(1)}><Chevron dir="right" size={14} /></button>
                                    </div>
                                    <div className={styles.calDow}>
                                        <div>월</div><div>화</div><div>수</div><div>목</div><div>금</div><div style={{ color: "#2b57c9" }}>토</div><div style={{ color: "#c93b2b" }}>일</div>
                                    </div>
                                    <div className={styles.calGrid}>
                                        {calCells.map((dn, i) => {
                                            if (dn === null) return <div key={"b" + i} style={{ height: 36 }} />;
                                            const k = dateKey(new Date(calY, calM, dn));
                                            const cls = k === fDate ? styles.calSel : k === todayKey ? styles.calToday : "";
                                            return (
                                                <button key={k} className={`${styles.calCell} ${cls}`} onClick={() => { setFDate(k); setCalOpen(false); }}>{dn}</button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                        <label className={styles.field}>시각
                            <input type="time" step={1800} className={styles.input} value={fTime} onChange={(e) => { setFTime(e.target.value); setErr(""); }} />
                        </label>
                        <div className={styles.field}>지점
                            <InterviewSelect value={fLocation} placeholder="지점 선택"
                                options={sunbaeLocations.map((l) => ({ value: l, label: l }))}
                                onChange={(v) => { setFLocation(v); setErr(""); }} />
                        </div>
                        <div className={styles.field}>근무
                            <InterviewSelect value={fJob}
                                options={JOBS.map((j) => ({ value: j, label: j }))}
                                onChange={(v) => setFJob(v)} />
                        </div>
                        <label className={styles.fieldGrow}>이름 (여러 명은 쉼표로)
                            <input type="text" className={styles.inputFull} value={fName} placeholder="홍길동, 김철수"
                                onChange={(e) => { setFName(e.target.value); setErr(""); }}
                                onKeyDown={(e) => { if (e.key === "Enter" && !(e.nativeEvent as any).isComposing) add(); }} />
                        </label>
                        <button className={styles.addBtn} onClick={add} disabled={adding}>추가</button>
                    </div>
                    {err ? <div className={styles.err}>{err}</div> : null}
                </section>

                {/* 주간 스케줄표 */}
                <section className={styles.card}>
                    <div className={styles.weekNav}>
                        <button className={styles.circleBtnLg} aria-label="이전 주" onClick={() => moveWeek(-1)}><Chevron dir="left" /></button>
                        <button className={styles.circleBtnLg} aria-label="다음 주" onClick={() => moveWeek(1)}><Chevron dir="right" /></button>
                        <div className={styles.rangeLabel}>{rangeLabel}</div>
                        <button className={styles.pillBtn} onClick={() => setWeekStart(dateKey(mondayOf(new Date())))}>이번 주</button>
                        <div className={styles.totalLabel}>이 주 면접 {items.length}명</div>
                    </div>
                    <div className={styles.weekScroll}>
                        <div className={styles.weekGrid}>
                            {days.map((day) => (
                                <div key={day.key} className={styles.dayCol}>
                                    {day.isToday
                                        ? <div className={styles.dayHeadToday}>{day.label} · 오늘</div>
                                        : <div className={styles.dayHead}>{day.label}</div>}
                                    <div className={styles.dayBody}>
                                        {day.groups.map((g) => (
                                            <div key={g.time} className={styles.group}>
                                                <div className={styles.groupHead}>
                                                    <div className={styles.groupLabel}>{fmtTime(g.time)}</div>
                                                    {g.people.length > 1 ? <div className={styles.groupCount}>{g.people.length}명</div> : null}
                                                </div>
                                                {g.people.map((p) => {
                                                    const evalLines = p.evaluations.filter(hasGrades);
                                                    return (
                                                        <button key={p.id} className={`${styles.chip} ${p.id === selId ? styles.chipSel : ""}`} onClick={() => setSelId(p.id)}>
                                                            <div className={styles.chipTop}>
                                                                <div className={styles.chipName}>{p.name}</div>
                                                                {p.status === 1 ? <div className={styles.badgeDone}>완료</div> : null}
                                                                {p.status === 2 ? <div className={styles.badgeNoShow}>노쇼</div> : null}
                                                            </div>
                                                            <div className={styles.chipSub}><span className={styles.loc}>{shortLocation(p.location)}</span> {p.job}</div>
                                                            {evalLines.map((ev) => (
                                                                <div key={ev.evaluatorId} className={styles.evalLine}>
                                                                    <span className={styles.gradeLine}>{gradeStr(ev)}</span>
                                                                    <span className={styles.evalWho}>({short3(ev.evaluatorName)})</span>
                                                                </div>
                                                            ))}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                        {day.groups.length === 0 ? <div className={styles.empty}>면접 없음</div> : null}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={styles.hint}>이름을 누르면 평가지 팝업이 열립니다. 등급 줄 옆 괄호는 평가한 사람입니다.</div>
                </section>

                <InterviewListModal
                    open={listOpen}
                    items={listItems}
                    since={listSince}
                    onOpen={(id) => setSelId(id)}
                    onClose={() => setListOpen(false)}
                />

                {selItem && (
                    <InterviewEvalModal
                        key={selItem.id}
                        item={selItem}
                        user={user}
                        onLocalChange={onLocalChange}
                        onDeleted={onDeleted}
                        onClose={() => setSelId(null)}
                    />
                )}
            </Box>
        </Modal>
    );
};

export default InterviewScheduleModal;
